import Vue from 'vue'
import VueRouter from 'vue-router'
import useJwt from '@/auth/jwt/useJwt'
import i18n from '@/libs/i18n'
import { getHomeRouteForLoggedInUser, getUserData } from '@/auth/utils'
import deploymentDetector from '@/utils/deploymentDetector'
import inactivityManager from '@/utils/inactivityManager'
// Routes
import usePermission from '../composable/usePermission'
import accounts from './routes/accounts'
import admin from './routes/admin'
import batches from './routes/batches'
import dashboard from './routes/dashboard'
import operations from './routes/operations'
import pages from './routes/pages'
import postings from './routes/postings'
import reports from './routes/reports'
import echannels from './routes/e-channels'
import fraudMonitor from './routes/fraud-monitor'
import lms from './routes/lms'
import testing from './routes/testing'
import FavoriteService from '../helpers/favoriteService'
import { storeOriginalUrl, getAndClearIntendedUrl, routeRequiresAuth } from '../helpers/misc'
import { checkTokenValidity } from '../helpers/token'
import store from '@/store'

Vue.use(VueRouter)

// Let inactivityManager check token expiry on wake from sleep
inactivityManager.setTokenExpiryChecker(() => {
  const token = useJwt.getToken()
  if (!token) return true
  try { return checkTokenValidity(token) } catch { return true }
})

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [
    {
      path: '/',
      redirect: { name: 'auth-login' },
    },
    ...accounts,
    ...admin,
    ...dashboard,
    ...operations,
    ...pages,
    ...postings,
    ...reports,
    ...batches,
    ...echannels,
    ...fraudMonitor,
    ...lms,
    ...testing,
    {
      path: '/*',
      redirect: 'error-404',
    },
  ],
})

if (process.env.NODE_ENV === 'production') {
  Promise.all([
    import(/* webpackChunkName: "sentry" */ '@sentry/vue'),
    import(/* webpackChunkName: "sentry" */ '@sentry/tracing'),
  ]).then(([Sentry, { BrowserTracing }]) => {
    Sentry.init({
      Vue,
      environment: process.env.BASE_URL,
      dsn: 'https://a3ac9acb56874e0190d3502f7c0d5da6@o947379.ingest.sentry.io/5896758',
      integrations: [
        new BrowserTracing({
          routingInstrumentation: Sentry.vueRouterInstrumentation(router),
          tracingOrigins: [process.env.BASE_URL, 'udara360.web', /^\//],
          tracePropagationTargets: [process.env.BASE_URL, 'udara360.web', /^\//],
        }),
      ],
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    })
  })
}

// Helper function to check if a route is an authentication page
const isAuthenticationPage = route => {
  // Check if it's explicitly marked as not requiring auth
  if (route.meta?.requiresAuth === false) {
    return true
  }

  // Check for common auth page patterns
  const authPageNames = [
    'auth-login',
    'verify-token',
    'auth-forgot-password',
    'password-verify-token',
    'security-question',
    'auth-reset-password-v1',
    'setup-security-questions',
    'reset-expired-password',
    'verified-system-access',
    'create-otp-factor',
    'error-404',
  ]

  // Check if route name matches auth patterns
  if (authPageNames.includes(route.name)) {
    return true
  }

  // Check if route has full layout and Auth resource (legacy check)
  if (route.meta?.layout === 'full' && route.meta?.resource === 'Auth') {
    return true
  }

  return false
}

router.beforeEach((to, from, next) => {
  const token = useJwt.getToken()
  const refreshToken = useJwt.getRefreshToken()
  const isLoggedIn = token && refreshToken
  const { getUserPermission } = usePermission()

  // Check if route is an authentication page
  // const isAuthPage = isAuthenticationPage(to)
  // const isAuthenticated = store.getters['auth/isLoggedIn']

  // Handle login with redirect query parameter
  if (to.path === '/login' && to.query.redirect) {
    const redirectPath = to.query.redirect
    storeOriginalUrl(redirectPath)
    return next({ name: 'auth-login' })
  }

  // Dynamic page title
  window.document.title = to.meta && to.meta.pageTitle ? i18n.t(to.meta.pageTitle) : 'Udara360'

  const requiresAuth = routeRequiresAuth(to)

  if (requiresAuth) {
    // Store the original URL for redirection after login
    if (!isLoggedIn && to.name !== 'auth-login') {
      storeOriginalUrl(to.fullPath)
      return next({ name: 'auth-login' })
    }

    if (to.meta.redirectIfLoggedIn && isLoggedIn) {
      const userData = getUserData()
      return next(getHomeRouteForLoggedInUser(userData ? userData.role : null))
    }

    if (!useJwt.checkTokenExpiration()) {
      if (!useJwt.checkRefreshTokenExpiration()) {
        // Store the URL before redirecting to login
        storeOriginalUrl(to.fullPath)
        return next({ name: 'auth-login' })
      }
      useJwt.refreshToken().then(r => {
        useJwt.isAlreadyFetchingAccessToken = false

        // Update accessToken in localStorage
        useJwt.setToken(r.data.accessToken)
        useJwt.setRefreshToken(r.data.refreshToken)

        useJwt.onAccessTokenFetched(r.data.accessToken)
      })
      return next()
    }
    if (to.meta.hasPermission) {
      if (getUserPermission(to.meta.hasPermission)) {
        return next()
      }
      return next({ name: 'misc-not-authorized' })
    }
    return next()
  }

  return next()
})

export const redirectAfterLogin = route => {
  const intendedUrl = getAndClearIntendedUrl()
  if (intendedUrl) {
    // Check if the intended URL is a valid route
    const matchedRoute = router.resolve(intendedUrl).route
    if (matchedRoute && matchedRoute.name !== '404') {
      return router.push(intendedUrl)
    }
  }

  // Check for user's preferred landing page
  const userPreferredLanding = localStorage.getItem('defaultLandingPage')
  if (userPreferredLanding) {
    // Verify the route exists before redirecting
    const preferredRoute = router.resolve({ name: userPreferredLanding }).route
    if (preferredRoute && preferredRoute.name !== '404') {
      return router.push({ name: userPreferredLanding })
    }
  }

  // Default route if no intended URL or URL is invalid
  // const userData = getUserData()

  if (route === '/admin-dashboard') {
    return router.push({ name: 'admin-dashboard' })
  }
  if (route === '/pfi-dashboard') {
    return router.push({ name: 'pfi-dashboard' })
  }
  return router.push({ name: 'home' })
}

// ? For splash screen
router.afterEach(to => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }

  // Inactivity Manager Control - Enable only on authenticated routes
  const isAuthPage = isAuthenticationPage(to)
  const isAuthenticated = store.getters['auth/isLoggedIn']
  const requiresAuth = routeRequiresAuth(to)

  if (isAuthenticated && requiresAuth && !isAuthPage) {
    // User is logged in and on a protected route - enable inactivity manager
    console.log('🔒 Enabling inactivity manager for protected route:', to.name)
    inactivityManager.enable()
  } else {
    // User is not logged in or on auth page - disable inactivity manager
    console.log('🔓 Disabling inactivity manager for route:', to.name, '(auth page or not logged in)')
    inactivityManager.disable()
  }

  // Track visit count for favourites
  if (to.name && to.meta && !to.meta.skipFavorites) {
    FavoriteService.recordInteraction({
      name: to.meta.pageTitle || to.name,
      route: to.name,
      icon: to.meta.icon || 'StarIcon',
    })
  }
})

// Handle router errors, especially chunk loading failures
router.onError(error => {
  console.error('Router error:', error)

  // Check if it's a chunk loading error
  if (
    error.message
    && (error.message.includes('Loading chunk')
     || error.message.includes('Loading CSS chunk')
     || error.message.includes('ChunkLoadError'))
  ) {
    console.log('Router chunk loading failed - marking update as available...')

    // Just mark update as available, don't interrupt user flow
    deploymentDetector.setUpdateAvailable(true)
  }
})

export default router
